import { useMemo, useState } from "react";
import {
  useClassificationRules,
  useCreateClassificationRule,
  useUpdateClassificationRule,
  useDeleteClassificationRule,
} from "../../../../queries";
import type {
  ClassificationRule,
  CreateClassificationRuleRequest,
  UpdateClassificationRuleRequest,
} from "@/services/api/classification-rules";
import type { SortField } from "../ClassificationRuleUtils";
import { sortClassificationRules } from "../ClassificationRuleUtils";
import { useConfigurationSearchParams } from "./useConfigurationSearchParams";
import { useUrlSortingSync } from "@/shared/hooks/useUrlSortingSync";

export function useClassificationRulesPanel() {
  const { data: rules, isLoading } = useClassificationRules();
  const createMutation = useCreateClassificationRule();
  const updateMutation = useUpdateClassificationRule();
  const deleteMutation = useDeleteClassificationRule();

  const { sorting: urlSorting, updateSearchParams } =
    useConfigurationSearchParams();

  const { localSorting, setLocalSorting } = useUrlSortingSync(
    urlSorting,
    updateSearchParams
  );

  const sortField = localSorting.sortField;
  const sortDirection = localSorting.sortDirection;
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<ClassificationRule | null>(
    null
  );
  const [deletingRule, setDeletingRule] = useState<ClassificationRule | null>(
    null
  );

  const sortedRules = useMemo<ClassificationRule[]>(() => {
    if (!rules) return [];
    return sortClassificationRules(rules, sortField, sortDirection);
  }, [rules, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setLocalSorting((prev) => ({
        ...prev,
        sortDirection: prev.sortDirection === "asc" ? "desc" : "asc",
      }));
    } else {
      setLocalSorting({ sortField: field, sortDirection: "asc" });
    }
  };

  const handleCreate = (data: CreateClassificationRuleRequest) => {
    createMutation.mutate(data, {
      onSuccess: () => {
        setIsCreateDialogOpen(false);
      },
    });
  };

  const handleUpdate = (
    id: string | number,
    data: UpdateClassificationRuleRequest
  ) => {
    updateMutation.mutate(
      { id, data },
      {
        onSuccess: () => {
          setEditingRule(null);
        },
      }
    );
  };

  const handleDelete = () => {
    if (!deletingRule) return;

    deleteMutation.mutate(deletingRule.id, {
      onSuccess: () => {
        setDeletingRule(null);
      },
    });
  };

  return {
    rules: sortedRules,
    isLoading,
    sortField,
    sortDirection,
    isCreateDialogOpen,
    editingRule,
    deletingRule,
    handleSort,
    handleCreate,
    handleUpdate,
    handleDelete,
    setIsCreateDialogOpen,
    setEditingRule,
    setDeletingRule,
    createIsLoading: createMutation.isPending,
    updateIsLoading: updateMutation.isPending,
    deleteIsLoading: deleteMutation.isPending,
    rulesCount: rules?.length,
  };
}
